import { useState } from "react";
import { Trash2 } from "lucide-react";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  SampleBaseData,
  samplesBaseDataSubdomain,
} from "@/services/backend/samples/types/sample-base-data";
import { useIsDefaultBaseData } from "@/services/backend/shared/base-data-kit/components/default-base-data-checkbox";

/**
 * The delete mutation of a sample base data kind, i.e. the return of e.g.
 * `useSamplesAirMicrobialBaseDataDeleteMutation()`. Only the members the
 * dialog relies on are described, so the RTK Query tuple is assignable.
 */
export type DeleteSampleBaseDataMutation = readonly [
  (request: { id: UUID }) => { unwrap: () => Promise<unknown> },
  { isLoading: boolean; error?: unknown; reset: () => void },
];

/**
 * Deletes a sample base data instance of the kind the mutation belongs to.
 * The kind specific dialogs pass their mutation and key, see e.g.
 * `DeleteAirMicrobialBaseDataDialog`.
 */
export function DeleteSampleBaseDataDialog({
  baseData,
  dataKey,
  mutation,
  onDeleted,
}: {
  baseData: SampleBaseData;
  /** The `basedatakit.Default` key of the kind, e.g. `airMicrobialBaseDataKey`. */
  dataKey: string;
  mutation: DeleteSampleBaseDataMutation;
  onDeleted?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [deleteBaseData, { isLoading, error, reset }] = mutation;

  // The backend refuses to delete the instance a company instantiates
  // from, so the dialog says so instead of sending a request that will
  // be rejected.
  const { isDefault } = useIsDefaultBaseData({
    subdomain: samplesBaseDataSubdomain,
    dataKey,
    companyId: baseData.companyId,
    id: baseData.id,
  });

  const onConfirm = () => {
    if (isLoading) {
      return;
    }
    deleteBaseData({ id: baseData.id })
      .unwrap()
      .then(() => {
        toast.success(t("Basisdaten gelöscht"));
        reset();
        setOpen(false);
        onDeleted?.();
      })
      // The error is rendered from the mutation state below.
      .catch(() => {});
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger icon={Trash2} label="" variant="destructive" />
      <DialogContent>
        <CommonDialogHeader
          title={t("Basisdaten wirklich löschen?")}
          description={
            isDefault
              ? t(
                  "Diese Basisdaten sind als aktiv markiert und können nicht gelöscht werden. Markieren Sie zuerst andere Basisdaten als aktiv.",
                )
              : t(
                  "Die Basisdaten werden gelöscht. Bereits erstellte Proben bleiben unverändert.",
                )
          }
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Löschen")}
          destructive
          disabled={isDefault}
        />
      </DialogContent>
    </Dialog>
  );
}
